import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-comparison",
  title: "A prayer for when you cannot stop comparing",
  h1: "A prayer for comparison and envy",
  description:
    "A prayer for the twenty minutes after somebody else's good news, four passages on envy, and why the feed makes an ancient problem worse.",
  targets: [
    "prayer for envy",
    "prayer about comparing myself to others",
    "prayer for jealousy",
    "prayer after seeing someone else succeed",
  ],
  published: "2026-09-03",
  standfirst:
    "Envy is the only one of the deadly sins nobody enjoys. It is also the one your phone is best at producing.",
  takeaways: [
    "Galatians 6:4 gives the practical instruction: examine your own work. The comparison stops when there is something else to measure against.",
    "2 Corinthians 10:12 calls measuring yourself by other people a lack of understanding — a category error rather than a moral lapse.",
    "You are comparing your ordinary Tuesday with somebody's edited highlight. That is not humility, it is bad arithmetic.",
  ],
  when: [
    "In the twenty minutes after somebody else's announcement.",
    "At the point in the evening where the feed has stopped being entertaining and started being a scoreboard.",
    "When you have congratulated somebody warmly and felt something else entirely.",
    "When you notice you have been checking one particular person's account.",
  ],
  prayer: [
    "God, they got the thing and I am not glad, and I have been pretending to be for about an hour.",
    "I do not actually want them to lose it. I want to stop needing them not to have it. Those are different and I keep confusing them.",
    "Show me my own work — the actual thing in front of me — because I cannot see it while I am looking at theirs.",
    "Take the specific bitterness out. Not the disappointment, which is fair. The bitterness, which is corroding something.",
    "And let me be genuinely pleased for them, eventually. Not tonight. But let that be the direction.",
  ],
  short:
    "God, I want to stop needing them not to have it. Show me my own work.",
  honest: [
    "This prayer does not ask for the disappointment to go. Wanting something you did not get is not a sin, and treating ordinary grief about it as envy is a good way to feel guilty on top of sad.",
    "It also does not promise you will be happy for them by the end of the evening. That usually takes longer, and forcing the performance early tends to bury the thing rather than resolve it.",
    "And it does not address the mechanism. If the comparison arrives through a feed you open forty times a day, no amount of praying about your heart will outrun the supply.",
  ],
  verses: [
    {
      verse: {
        text: "But let each man examine his own work, and then he will have reason to boast in himself, and not in someone else.",
        ref: "Galatians 6:4",
      },
      note: "The remedy offered is not humility but a change of measuring stick, which is far more practical. Look at the actual work in front of you and assess that. The verse assumes there will be something to be pleased about, and it locates the pleasure in the work rather than in the ranking — which is the whole difference.",
    },
    {
      verse: {
        text: "But they themselves, measuring themselves by themselves, and comparing themselves with themselves, are without understanding.",
        ref: "2 Corinthians 10:12",
      },
      note: "Paul is being sarcastic about rivals who keep score against each other, and the verdict he reaches is intellectual rather than moral: they are without understanding. Comparison is treated here as a mistake about how reality works, like using the wrong unit, rather than as wickedness.",
    },
    {
      verse: {
        text: "The life of the body is a heart at peace, but envy rots the bones.",
        ref: "Proverbs 14:30",
      },
      note: "The image is physical and slow. Not a fire — rot, which works from the inside over a long time and is invisible until something structural gives way. Proverbs is not warning that envy will make you behave badly. It is warning about what it does to the person carrying it, whether or not anybody ever notices.",
    },
    {
      verse: {
        text: "For where jealousy and selfish ambition are, there is confusion and every evil deed.",
        ref: "James 3:16",
      },
      note: "Confusion is the word to sit with — disorder, instability, things not in their places. James is describing a community rather than an individual, and the claim is that envy does not stay private: it reorganises how a group works, and the reorganisation is what lets everything else in.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "An ancient problem with a new supply chain",
      id: "an-ancient-problem-with-a-new-supply-chain",
    },
    {
      type: "p",
      text: "Envy is old. Cain, Rachel, Saul, the elder brother. What is new is the volume: comparison used to arrive at a rate set by how many people you actually saw, and now it arrives at a rate set by a company that profits from your staying.",
    },
    {
      type: "p",
      text: "The moral instruction has not changed. The dosage has, and any honest attempt at this has to deal with {the supply} rather than only with the response.",
      links: {
        "the supply": "/verses/bible-verses-about-comparison",
      },
    },
    {
      type: "h2",
      text: "Examine your own work",
      id: "examine-your-own-work",
    },
    {
      type: "p",
      text: "Galatians 6:4 is unusually actionable for a verse about a feeling. It does not say stop comparing; it says go and look at what you are actually doing, in enough detail to have an opinion about it.",
    },
    {
      type: "p",
      text: "In practice that means having something to measure against that is not a person — a piece of work, a stretch of time, last year's version of yourself. Comparison thrives in the absence of any other standard.",
    },
    {
      type: "h2",
      text: "The arithmetic is wrong",
      id: "the-arithmetic-is-wrong",
    },
    {
      type: "p",
      text: "You are comparing everything you know about yourself with the part of somebody else that was chosen for publication. Not their week — the four seconds of it that survived editing.",
    },
    {
      type: "note",
      text: "This is a genuinely useful thing to say to yourself, and it wears off within about a minute of continued scrolling. It is a correction, not a defence.",
    },
  ],
  faq: [
    {
      q: "What can I pray when I am jealous of someone?",
      a: '"God, I want to stop needing them not to have it." That names the actual thing more accurately than asking to be happy for them, which usually cannot be produced on demand.',
    },
    {
      q: "Which Bible verse is about comparison?",
      a: "Galatians 6:4 for the practical instruction — examine your own work — and 2 Corinthians 10:12, where measuring yourself by other people is called a lack of understanding rather than a sin.",
    },
    {
      q: "How do I stop comparing myself on social media?",
      a: "Reduce the supply before you work on the response. The moral instruction has not changed since Cain; the number of comparisons arriving per day has, and no amount of praying about your heart will outrun a feed you open forty times.",
    },
  ],
  relatedPrayers: ["prayer-for-gratitude", "prayer-for-anger"],
  related: ["social-media-fast-guide", "dopamine-detox-christian"],
  relatedTopics: ["bible-verses-about-comparison", "bible-verses-about-peace"],
  relatedPassages: ["romans-12-2"],
};
